// Shared PostingModule labels + the "who may post here" rule, so the account picker, the create modal
// and the edit modal all describe controlled accounts the same way.
import { FinancialAccount, PostingModule } from '../models/financial-account/financial-account';

export { PostingModule };

/** Fallback (English) labels. Callers with a TranslateService should prefer `postingModuleOptions`. */
export const POSTING_MODULE_LABELS: Record<PostingModule, string> = {
  [PostingModule.GeneralLedger]: 'General ledger',
  [PostingModule.AccountsPayable]: 'Accounts payable',
  [PostingModule.AccountsReceivable]: 'Accounts receivable',
  [PostingModule.Banking]: 'Banking',
  [PostingModule.PettyCash]: 'Petty cash',
  [PostingModule.FixedAssets]: 'Fixed assets',
  [PostingModule.CashManagement]: 'Cash management',
};

/** Translation key per module, so labels follow the user's language where a TranslateService is available. */
export const POSTING_MODULE_KEYS: Record<PostingModule, string> = {
  [PostingModule.GeneralLedger]: 'PostingModule_GL',
  [PostingModule.AccountsPayable]: 'PostingModule_AP',
  [PostingModule.AccountsReceivable]: 'PostingModule_AR',
  [PostingModule.Banking]: 'PostingModule_Banking',
  [PostingModule.PettyCash]: 'PostingModule_PettyCash',
  [PostingModule.FixedAssets]: 'PostingModule_FixedAssets',
  [PostingModule.CashManagement]: 'PostingModule_CashManagement',
};

/** Every module, in enum order — the option list for the create/edit multiselects. */
export const POSTING_MODULES: PostingModule[] = [
  PostingModule.GeneralLedger,
  PostingModule.AccountsPayable,
  PostingModule.AccountsReceivable,
  PostingModule.Banking,
  PostingModule.PettyCash,
  PostingModule.FixedAssets,
  PostingModule.CashManagement,
];

/** Build { label, value } options, translated when a translate function is supplied. */
export function postingModuleOptions(t?: (key: string, fallback: string) => string): { label: string; value: PostingModule }[] {
  return POSTING_MODULES.map((value) => ({
    label: t ? t(POSTING_MODULE_KEYS[value], POSTING_MODULE_LABELS[value]) : POSTING_MODULE_LABELS[value],
    value,
  }));
}

export function postingModuleLabel(module: PostingModule, t?: (key: string, fallback: string) => string): string {
  return t ? t(POSTING_MODULE_KEYS[module], POSTING_MODULE_LABELS[module]) : POSTING_MODULE_LABELS[module];
}

/**
 * Whether `module` may post to `account`.
 *
 * <p>An EMPTY `authorizedPostingModules` means the account is freely postable; only a non-empty list
 * restricts it. This mirrors JournalEntryPostingValidator server-side — testing membership alone would
 * treat the empty default as a denial and hide every ordinary revenue and expense account.</p>
 */
export function allowsPostingFrom(account: Pick<FinancialAccount, 'authorizedPostingModules'>, module: PostingModule): boolean {
  const controlledBy = account.authorizedPostingModules ?? [];
  return controlledBy.length === 0 || controlledBy.includes(module);
}
